"use client";

import { useEffect, useRef, useState } from "react";
import { FaInstagram } from "./icons";
import { instagramPostUrl } from "@/lib/instagram";

let scriptPromise: Promise<void> | undefined;
function loadInstagram(): Promise<void> {
  if (window.instgrm) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://www.instagram.com/embed.js";
    script.async = true;
    const timer = window.setTimeout(() => {
      script.remove();
      reject(new Error("timeout"));
    }, 10000);
    script.onload = () => {
      window.clearTimeout(timer);
      window.instgrm ? resolve() : reject(new Error("unavailable"));
    };
    script.onerror = () => {
      window.clearTimeout(timer);
      script.remove();
      reject(new Error("unavailable"));
    };
    document.body.appendChild(script);
  }).catch((error) => {
    scriptPromise = undefined;
    throw error;
  });
  return scriptPromise;
}

export default function InstagramEmbed({
  postUrl,
  caption,
  className = "",
}: {
  postUrl: string;
  caption?: string;
  className?: string;
}) {
  const url = instagramPostUrl(postUrl);
  const [active, setActive] = useState(false);
  const [failed, setFailed] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!active || !url) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (!cancelled && !container.current?.querySelector("iframe"))
        setFailed(true);
    }, 15000);
    loadInstagram()
      .then(() => {
        if (!cancelled) window.instgrm?.Embeds.process();
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [active, url]);
  if (!url) return null;
  return (
    <figure className={`not-prose my-10 min-w-0 ${className}`}>
      {!active || failed ? (
        <div className="border border-line-strong bg-surface/60 px-5 py-6 sm:px-7">
          <div className="flex items-center gap-3 text-ink">
            <FaInstagram size={22} aria-hidden="true" />
            <span className="text-sm font-bold">From Instagram</span>
          </div>
          {caption && (
            <p className="mt-3 text-sm leading-6 text-ink-secondary">{caption}</p>
          )}
          <p className="mt-2 text-sm leading-6 text-muted">
            {failed
              ? "Instagram could not load this post. You can still open it directly."
              : "View the post in this story. Instagram loads when you choose to open it."}
          </p>
          {!failed && (
            <button
              type="button"
              onClick={() => setActive(true)}
              className="mt-4 min-h-11 bg-theme-neon px-5 py-2 text-sm font-bold text-on-accent hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent"
            >
              Load Instagram post
            </button>
          )}
        </div>
      ) : (
        <div
          ref={container}
          className="editorial-instagram mx-auto w-full max-w-[540px] min-w-0 overflow-hidden"
        >
          <blockquote
            className="instagram-media"
            data-instgrm-permalink={url}
            data-instgrm-version="14"
            data-instgrm-captioned=""
            style={{ margin: 0, width: "100%", minWidth: 0 }}
          >
            <a href={url} target="_blank" rel="noopener noreferrer">
              View this post on Instagram
            </a>
          </blockquote>
        </div>
      )}
      <figcaption className="mt-3 text-xs leading-5 text-muted">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 hover:text-accent"
        >
          Open the original Instagram post ↗
        </a>
      </figcaption>
    </figure>
  );
}

declare global {
  interface Window {
    instgrm?: { Embeds: { process: () => void } };
  }
}
